import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, RefreshCw, Loader2, Check, Camera } from 'lucide-react';
import { Button } from './ui/button';
import api from '../services/api';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const withAuth = (path) => {
  if (!path) return null;
  const token = localStorage.getItem('hpme_token') || '';
  const sep = path.includes('?') ? '&' : '?';
  return `${BACKEND_URL}${path}${sep}auth=${token}`;
};

/**
 * WellnessAvatarCard (Profile/Settings) — TWO-STEP regenerate from the saved
 * selfie: render the base portrait first, let the user APPROVE the likeness,
 * then build the 16 world scenes. Mirrors the onboarding flow so a regenerate
 * never silently burns a full paid run on a bad likeness.
 */
const WellnessAvatarCard = () => {
  const [status, setStatus] = useState(null);
  const [baseUrl, setBaseUrl] = useState(null);
  const poll = useRef(null);

  const stopPoll = () => { if (poll.current) { clearInterval(poll.current); poll.current = null; } };

  const refresh = async () => {
    try {
      const r = await api.getAvatarStatus();
      setStatus(r.data);
      if (r.data?.base_url) setBaseUrl(withAuth(r.data.base_url));
      return r.data;
    } catch { return null; }
  };

  useEffect(() => { refresh(); return () => stopPoll(); }, []);

  const startPolling = () => {
    stopPoll();
    poll.current = setInterval(async () => {
      const s = await refresh();
      if (!s) return;
      if (s.status === 'ready') {
        stopPoll();
        window.dispatchEvent(new Event('avatar:changed'));
        window.dispatchEvent(new Event('wellness:refresh'));
        toast.success('Your avatar is ready!');
      } else if (s.status === 'failed') {
        stopPoll();
        toast.error('Generation failed — try again.');
      }
    }, 4000);
  };

  const startBase = async () => {
    try {
      await api.regenerateAvatarBase();
      await refresh();
      startPolling();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No saved selfie — upload one from your dashboard first.');
    }
  };

  const confirmBase = async () => {
    try { await api.confirmAvatarBase(); await refresh(); startPolling(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not continue.'); }
  };

  const st = status?.status || 'none';
  const generatingBase = st === 'generating_base';
  const generatingStates = st === 'generating_states';
  const busy = generatingBase || generatingStates;
  const pct = generatingStates && status?.total ? Math.round((status.progress / status.total) * 100) : 0;

  return (
    <div className="hpme-card p-6" data-testid="wellness-avatar-card">
      <div className="hpme-card-corner" />
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5 text-primary" />
        <h3 className="font-secondary font-bold text-lg tracking-tight">Wellness Avatar</h3>
      </div>
      <p className="text-sm text-zinc-400 mb-4">
        Your personalised cinematic character across all 16 world states.
        Status: <span className="text-zinc-200 font-semibold">{st}</span>
      </p>

      {generatingBase && (
        <div className="mb-4 flex items-center gap-2 text-sm text-zinc-300" data-testid="avatar-base-progress">
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
          Rendering your character… (~1 min, runs in the background — safe to leave)
        </div>
      )}

      {st === 'base_ready' && (
        <div className="mb-4" data-testid="avatar-approve-block">
          <p className="text-sm text-zinc-300 mb-3">Does this look like you? Approve to build your worlds, or re-render.</p>
          {baseUrl && <img src={baseUrl} alt="Your character" className="w-40 h-52 object-cover rounded-xl border border-white/15 mb-3" data-testid="avatar-base-img" />}
          <div className="flex flex-wrap gap-2">
            <Button onClick={confirmBase} className="bg-primary text-black flex items-center gap-2" data-testid="avatar-approve-btn">
              <Check className="w-4 h-4" /> Build my worlds
            </Button>
            <Button onClick={startBase} variant="outline" className="border-zinc-700 flex items-center gap-2" data-testid="avatar-reroll-btn">
              <RefreshCw className="w-4 h-4" /> Try again
            </Button>
          </div>
          <p className="text-[11px] text-zinc-500 mt-2">Building the 16 worlds takes about 3–5 minutes.</p>
        </div>
      )}

      {generatingStates && (
        <div className="mb-4" data-testid="avatar-progress">
          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-xs text-zinc-500 mt-1.5">{status.progress}/{status.total} worlds… (3–5 min, runs in the background — safe to leave)</p>
        </div>
      )}

      {st !== 'base_ready' && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-zinc-500">Re-render your character first, approve it, then build the worlds.</p>
          <Button onClick={startBase} disabled={busy} variant="outline"
            className="border-zinc-700 flex items-center gap-2 shrink-0" data-testid="avatar-regenerate-btn">
            {generatingBase ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            Regenerate
          </Button>
        </div>
      )}
    </div>
  );
};

export default WellnessAvatarCard;
